/**
 * Import des graines 2FA d'une **application d'authentification** : Aegis
 * (export JSON non chiffre), 2FAS (sauvegarde `.2fas` non chiffree), Google
 * Authenticator (« Transferer des comptes » : URI `otpauth-migration://`) et
 * Proton Authenticator (export JSON, chiffre compris) et toute liste d'URI
 * `otpauth://` (export texte d'Aegis, de 2FAS, d'Ente Auth -- son
 * `codeDisplay` compris : epingle, etiquettes, note, corbeille --, de
 * Bitwarden Authenticator...). Klavox garde les graines dans le coffre
 * chiffre, ce qui evite de les perdre avec le telephone.
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry` (un identifiant par compte : nom = emetteur, identifiant =
 * compte, graine TOTP). Une graine TOTP aux parametres non standard
 * (periode, chiffres, algorithme) est gardee en URI `otpauth://` entiere
 * (`normalizeTotpSecret`), sinon le code calcule serait faux.
 *
 * Steam Guard est calcule (graine `steam://`, code a 5 caracteres) comme chez
 * Bitwarden, Aegis et 2FAS.
 *
 * Aucune perte : une graine que Klavox ne sait pas calculer (HOTP a compteur,
 * mOTP, Yandex, MD5, parametre hors borne) devient un champ **masque**
 * de l'entree, en URI rejouable, jamais une graine fausse ni une note en
 * clair a l'ecran.
 *
 * Une sauvegarde **chiffree** se lit avec SON mot de passe, demande par l'UI
 * (`ImportPasswordRequiredError`) et jamais conserve : Aegis (scrypt +
 * AES-256-GCM, cle maitre scellee par emplacement, comme `docs/decrypt.py`
 * d'Aegis) et 2FAS (PBKDF2-HMAC-SHA256 10 000 tours + AES-256-GCM, comme
 * l'importeur 2FAS d'Aegis) et Proton Authenticator (Argon2id + AES-256-GCM,
 * comme l'importeur Proton d'Aegis). INVARIANT CLAUDE.md : aucune crypto maison, les
 * primitives viennent de `@noble/*` (memes bibliotheques que les imports
 * KeePass et Bitwarden) ou de WebCrypto ; un tag GCM refuse ne livre jamais rien.
 *
 * La derivation ne fige pas l'onglet : scrypt (Aegis) et Argon2id (Proton
 * Authenticator) passent par les variantes asynchrones de la meme
 * bibliotheque, qui rendent la main au navigateur et disent leur avancement ;
 * PBKDF2 (2FAS) passe par WebCrypto (natif, hors du fil principal), comme
 * l'import Bitwarden.
 */
import { gcm } from '@noble/ciphers/aes.js';
import { argon2idAsync } from '@noble/hashes/argon2.js';
import { scryptAsync } from '@noble/hashes/scrypt.js';
import { base64ToBytes, bytesToBase32, bytesToUtf8, concatBytes, hexToBytes, parseTotpSeed, utf8ToBytes, wipe, type EntryCustomField } from '@klavox/crypto-core';
import { ImportPasswordInvalidError, ImportPasswordRequiredError, normalizeTotpSecret, type ImportedEntry } from './csv-import.js';

type Json = unknown;

function asObject(value: Json): Record<string, Json> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, Json>) : null;
}

function asString(value: Json): string {
  return typeof value === 'string' ? value.trim() : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
}

/** Compte d'authentificateur, quel que soit le format d'origine. */
interface AuthenticatorAccount {
  /** `totp`, `hotp`, `steam`, `motp`, `yandex`... en minuscules. */
  readonly kind: string;
  readonly issuer: string;
  readonly account: string;
  /** Graine base32. */
  readonly secret: string;
  /** `SHA1`, `SHA256`, `SHA512`, `MD5`... ; vide = defaut. */
  readonly algorithm: string;
  readonly digits: string;
  readonly period: string;
  readonly counter: string;
  readonly notes: string;
  readonly favorite: boolean;
  readonly tags: readonly string[];
  /** Compte mis a la corbeille dans l'authentificateur (Ente Auth) : importe aux archives, jamais perdu. */
  readonly archived?: boolean;
}

/**
 * URI `otpauth://` du compte : la forme que tout authentificateur relit
 * (Key URI Format). Les parametres par defaut sont omis.
 */
function accountUri(account: AuthenticatorAccount): string {
  const label = account.issuer.length > 0 && account.account.length > 0 ? `${account.issuer}:${account.account}` : account.issuer || account.account;
  const params = new URLSearchParams();
  params.set('secret', account.secret.replace(/[\s-]+/gu, '').replace(/=+$/u, '').toUpperCase());
  if (account.issuer.length > 0) params.set('issuer', account.issuer);
  if (account.algorithm.length > 0 && account.algorithm.toUpperCase() !== 'SHA1') params.set('algorithm', account.algorithm.toUpperCase());
  if (account.digits.length > 0 && account.digits !== '6') params.set('digits', account.digits);
  if (account.kind === 'hotp') params.set('counter', account.counter || '0');
  else if (account.period.length > 0 && account.period !== '30') params.set('period', account.period);
  return `otpauth://${encodeURIComponent(account.kind)}/${encodeURIComponent(label)}?${params.toString()}`;
}

/**
 * Graine Steam Guard `steam://<base32>`, la forme de Bitwarden : Steam ne
 * connait que SHA-1 sur 30 s, tout autre parametre est garde en champ masque.
 */
function steamSeedOf(account: AuthenticatorAccount): string | null {
  if (!['', 'SHA1'].includes(account.algorithm.toUpperCase()) || !['', '30'].includes(account.period)) return null;
  const seed = `steam://${account.secret.replace(/[\s-]+/gu, '').replace(/=+$/u, '').toUpperCase()}`;
  try {
    parseTotpSeed(seed);
  } catch {
    return null;
  }
  return seed;
}

/** Graine calculable par Klavox (`parseTotpSeed` l'accepte) ; sinon `null`. */
function totpSeedOf(account: AuthenticatorAccount, uri: string): string | null {
  if (account.kind === 'steam' && account.secret.length > 0) return steamSeedOf(account);
  if (account.kind !== 'totp' || account.secret.length === 0) return null;
  try {
    parseTotpSeed(uri);
  } catch {
    return null;
  }
  return normalizeTotpSecret(uri).secret;
}

function accountEntry(account: AuthenticatorAccount): ImportedEntry | null {
  if (account.secret.length === 0 && account.issuer.length === 0 && account.account.length === 0) return null;
  const uri = accountUri(account);
  const totpSecret = totpSeedOf(account, uri);
  const customFields: EntryCustomField[] =
    totpSecret === null && account.secret.length > 0
      ? [{ name: `Graine ${account.kind.toUpperCase()} (non calculee par Klavox)`, value: uri, hidden: true }]
      : [];
  return {
    type: 'login',
    name: account.issuer || account.account || 'Sans nom',
    username: account.account,
    password: '',
    uris: [],
    notes: account.notes,
    totpSecret: totpSecret ?? '',
    favorite: account.favorite,
    ...(customFields.length > 0 ? { customFields } : {}),
    ...(account.tags.length > 0 ? { tags: account.tags } : {}),
    ...(account.archived === true ? { archived: true } : {}),
  };
}

function entriesOf(accounts: readonly AuthenticatorAccount[]): ImportedEntry[] {
  return accounts.map(accountEntry).filter((entry): entry is ImportedEntry => entry !== null);
}

/** Octets d'un champ du fichier (hex ou base64), a la longueur attendue ; sinon fichier refuse. */
function fieldBytes(value: Json, encoding: 'hex' | 'base64', what: string, length?: number): Uint8Array {
  let bytes: Uint8Array;
  try {
    bytes = encoding === 'hex' ? hexToBytes(asString(value)) : base64ToBytes(asString(value));
  } catch {
    throw new Error(`${what} : structure inattendue`);
  }
  if (bytes.length === 0 || (length !== undefined && bytes.length !== length)) throw new Error(`${what} : structure inattendue`);
  return bytes;
}

/**
 * AES-256-GCM, tag a part (Aegis) ou colle au chiffre (2FAS) ; `null` si le
 * tag est refuse -- l'appelant dit pourquoi, rien n'est jamais rendu a moitie.
 */
function openGcm(key: Uint8Array, nonce: Uint8Array, sealed: Uint8Array, aad?: Uint8Array): Uint8Array | null {
  try {
    return gcm(key, nonce, aad).decrypt(sealed);
  } catch {
    return null;
  }
}

// --- Aegis -------------------------------------------------------------------

/**
 * Plafonds scrypt d'un emplacement Aegis : Aegis derive avec N = 2^15, r = 8,
 * p = 1 (32 Mio). Un fichier forge ne doit pas faire geler ni saturer l'onglet.
 */
const AEGIS_SCRYPT_MAX_N = 2 ** 20;
const AEGIS_SCRYPT_MAX_R = 32;
const AEGIS_SCRYPT_MAX_P = 16;
const AEGIS_SCRYPT_MAX_MEMORY = 256 * 1024 * 1024;

/**
 * Emplacements « mot de passe » essayes au plus : Aegis n'en ecrit qu'un (le
 * changement de mot de passe le remplace). Sans plafond, un fichier forge
 * empilerait les derivations a 256 Mio et tiendrait l'onglet des minutes.
 */
const AEGIS_MAX_PASSWORD_SLOTS = 4;

/** Temps maximal (ms) pendant lequel une derivation garde la main avant de la rendre au navigateur. */
const KDF_ASYNC_TICK_MS = 100;

/** Emplacement de type 1 (mot de passe) ; 0 = cle brute, 2 = biometrie : illisibles hors de l'appareil. */
const AEGIS_PASSWORD_SLOT = 1;

/**
 * Octets du mot de passe tels qu'Aegis les derivait avant sa correction du
 * probleme #95 (commit afb9e59) : `CryptoUtils.toBytesOld` rend TOUT le tableau
 * de l'encodeur UTF-8 d'Android, zeros de fin compris. Ce tableau mesure
 * `(int)(longueur UTF-16 * 2.0)` octets (moyenne ICU (3 + 1) / 2,
 * `NativeConverter_getAveBytesPerChar`), porte a `2n + 1` tant que l'encodage
 * deborde (`CharsetEncoder.encode`). Jusqu'a 64 octets, HMAC complete la cle
 * par des zeros : la derivation est identique et Aegis ne retente rien ;
 * au-dela, la cle differe.
 */
function aegisLegacyPasswordBytes(encoded: Uint8Array, password: string): Uint8Array {
  let capacity = password.length * 2;
  while (capacity < encoded.length) capacity = 2 * capacity + 1;
  const legacy = new Uint8Array(capacity);
  legacy.set(encoded);
  return legacy;
}

/**
 * Rend la base en clair d'une sauvegarde Aegis chiffree : le mot de passe
 * ouvre la cle maitre d'un emplacement « mot de passe », la cle maitre ouvre
 * `db`. Les cles derivees et la cle maitre sont effacees apres usage.
 *
 * @throws {ImportPasswordRequiredError} mot de passe non fourni
 * @throws {ImportPasswordInvalidError} aucun emplacement ne s'ouvre
 */
async function decryptAegisDb(header: Record<string, Json>, sealedDb: Json, password: string | undefined, onProgress?: (fraction: number) => void): Promise<Json> {
  const slots = (Array.isArray(header['slots']) ? header['slots'] : []).map(asObject).filter((slot) => slot?.['type'] === AEGIS_PASSWORD_SLOT);
  if (slots.length === 0) {
    throw new Error('sauvegarde Aegis chiffree sans mot de passe (biometrie seule) : definissez un mot de passe dans Aegis, ou exportez sans chiffrement');
  }
  if (slots.length > AEGIS_MAX_PASSWORD_SLOTS) throw new Error('sauvegarde Aegis : trop d’emplacements « mot de passe », fichier refuse');
  if (password === undefined) throw new ImportPasswordRequiredError('Aegis chiffre');
  // Chaque emplacement a sa part de l'avancement ; jamais de recul (un essai par l'ancien encodage reste dans sa part).
  let reported = 0;
  const report = (fraction: number): void => {
    if (fraction <= reported) return;
    reported = fraction;
    onProgress?.(fraction);
  };
  const params = asObject(header['params']);
  const dbNonce = fieldBytes(params?.['nonce'], 'hex', 'sauvegarde Aegis', 12);
  const dbTag = fieldBytes(params?.['tag'], 'hex', 'sauvegarde Aegis', 16);
  const db = fieldBytes(sealedDb, 'base64', 'sauvegarde Aegis');
  const secret = utf8ToBytes(password);
  let masterKey: Uint8Array | null = null;
  try {
    for (const [index, slot] of (slots as Record<string, Json>[]).entries()) {
      const n = slot['n'];
      const r = slot['r'];
      const p = slot['p'];
      if (
        typeof n !== 'number' || !Number.isInteger(n) || n < 2 || n > AEGIS_SCRYPT_MAX_N || (n & (n - 1)) !== 0 ||
        typeof r !== 'number' || !Number.isInteger(r) || r < 1 || r > AEGIS_SCRYPT_MAX_R ||
        typeof p !== 'number' || !Number.isInteger(p) || p < 1 || p > AEGIS_SCRYPT_MAX_P ||
        128 * r * (n + p) > AEGIS_SCRYPT_MAX_MEMORY
      ) {
        throw new Error('sauvegarde Aegis : parametres de derivation hors borne');
      }
      const keyParams = asObject(slot['key_params']);
      const sealedKey = concatBytes(fieldBytes(slot['key'], 'hex', 'sauvegarde Aegis', 32), fieldBytes(keyParams?.['tag'], 'hex', 'sauvegarde Aegis', 16));
      const slotNonce = fieldBytes(keyParams?.['nonce'], 'hex', 'sauvegarde Aegis', 12);
      const salt = fieldBytes(slot['salt'], 'hex', 'sauvegarde Aegis');
      const derived = await scryptAsync(secret, salt, {
        N: n,
        r,
        p,
        dkLen: 32,
        maxmem: AEGIS_SCRYPT_MAX_MEMORY + 1024,
        asyncTick: KDF_ASYNC_TICK_MS,
        onProgress: (fraction) => report((index + fraction) / slots.length),
      });
      try {
        masterKey = openGcm(derived, slotNonce, sealedKey);
      } finally {
        wipe(derived);
      }
      if (masterKey !== null) break;
      // Emplacement jamais « repare » par Aegis (`repaired` absent = faux, comme
      // `optBoolean`) : un mot de passe long a pu y etre derive par l'ancien encodage.
      if (slot['repaired'] === true) continue;
      const legacy = aegisLegacyPasswordBytes(secret, password);
      try {
        if (legacy.length <= 64) continue;
        const legacyDerived = await scryptAsync(legacy, salt, { N: n, r, p, dkLen: 32, maxmem: AEGIS_SCRYPT_MAX_MEMORY + 1024, asyncTick: KDF_ASYNC_TICK_MS });
        try {
          masterKey = openGcm(legacyDerived, slotNonce, sealedKey);
        } finally {
          wipe(legacyDerived);
        }
      } finally {
        wipe(legacy);
      }
      if (masterKey !== null) break;
    }
    if (masterKey === null) throw new ImportPasswordInvalidError('Aegis');
    report(1);
    const plain = openGcm(masterKey, dbNonce, concatBytes(db, dbTag));
    // La cle maitre s'est ouverte : un `db` refuse est un fichier altere, pas un mauvais mot de passe.
    if (plain === null) throw new Error('sauvegarde Aegis alteree : le contenu ne correspond pas a sa signature, rien n’a ete importe');
    try {
      return JSON.parse(bytesToUtf8(plain)) as Json;
    } catch {
      throw new Error('sauvegarde Aegis : contenu dechiffre illisible');
    } finally {
      wipe(plain);
    }
  } finally {
    wipe(secret, masterKey);
  }
}


/**
 * Un export Aegis se reconnait a son enveloppe `version` + `header` + `db` :
 * aucun autre format lu par Klavox ne porte ces trois cles.
 */
export function isAegisExport(data: Json): boolean {
  const file = asObject(data);
  return file !== null && typeof file['version'] === 'number' && asObject(file['header']) !== null && 'db' in file;
}

/**
 * Parse un export Aegis, en clair (`db` objet) ou chiffre (`db` chaine,
 * ouverte avec `password`) : chaque entree devient un identifiant ; ses
 * groupes (base v3 : `groups` d'identifiants, base v2 : `group` nomme)
 * deviennent des etiquettes.
 *
 * @throws {ImportPasswordRequiredError} sauvegarde chiffree sans mot de passe
 * @throws {ImportPasswordInvalidError} mot de passe refuse
 */
export async function parseAegisExport(data: Json, password?: string, onProgress?: (fraction: number) => void): Promise<ImportedEntry[]> {
  const file = asObject(data);
  const header = asObject(file?.['header']);
  if (file === null || header === null) throw new Error('export Aegis : structure inattendue');
  const db = asObject(typeof file['db'] === 'string' ? await decryptAegisDb(header, file['db'], password, onProgress) : file['db']);
  if (db === null || !Array.isArray(db['entries'])) throw new Error('export Aegis : structure inattendue');
  const groupNames = new Map<string, string>();
  for (const group of Array.isArray(db['groups']) ? db['groups'] : []) {
    const record = asObject(group);
    if (record !== null && asString(record['uuid']).length > 0) groupNames.set(asString(record['uuid']), asString(record['name']));
  }
  const accounts = (db['entries'] as readonly Json[])
    .map(asObject)
    .filter((entry): entry is Record<string, Json> => entry !== null)
    .map((entry): AuthenticatorAccount => {
      const info = asObject(entry['info']) ?? {};
      const groups = Array.isArray(entry['groups']) ? entry['groups'].map((id) => groupNames.get(asString(id)) ?? '') : [asString(entry['group'])];
      return {
        kind: asString(entry['type']).toLowerCase() || 'totp',
        issuer: asString(entry['issuer']),
        account: asString(entry['name']),
        secret: asString(info['secret']),
        algorithm: asString(info['algo']),
        digits: asString(info['digits']),
        period: asString(info['period']),
        counter: asString(info['counter']),
        notes: typeof entry['note'] === 'string' ? entry['note'].trim() : '',
        favorite: entry['favorite'] === true,
        tags: groups.filter((name) => name.length > 0),
      };
    });
  return entriesOf(accounts);
}

// --- 2FAS --------------------------------------------------------------------

/**
 * Une sauvegarde 2FAS se reconnait au couple `services` (tableau) +
 * `schemaVersion` (nombre).
 */
export function isTwoFasExport(data: Json): boolean {
  const file = asObject(data);
  return file !== null && Array.isArray(file['services']) && typeof file['schemaVersion'] === 'number';
}

/**
 * Parse une sauvegarde 2FAS, en clair ou chiffree (`servicesEncrypted`,
 * ouvert avec `password`) : chaque service devient un identifiant, son groupe
 * une etiquette.
 *
 * @throws {ImportPasswordRequiredError} sauvegarde chiffree sans mot de passe
 * @throws {ImportPasswordInvalidError} mot de passe refuse
 */
export async function parseTwoFasExport(data: Json, password?: string, onProgress?: (fraction: number) => void): Promise<ImportedEntry[]> {
  const file = asObject(data);
  if (file === null || !Array.isArray(file['services'])) throw new Error('sauvegarde 2FAS : structure inattendue');
  const sealed = asString(file['servicesEncrypted']);
  const services = sealed.length > 0 ? await decryptTwoFasServices(sealed, password, onProgress) : (file['services'] as readonly Json[]);
  const groupNames = new Map<string, string>();
  for (const group of Array.isArray(file['groups']) ? file['groups'] : []) {
    const record = asObject(group);
    if (record !== null && asString(record['id']).length > 0) groupNames.set(asString(record['id']), asString(record['name']));
  }
  const accounts = services
    .map(asObject)
    .filter((service): service is Record<string, Json> => service !== null)
    .map((service): AuthenticatorAccount => {
      const otp = asObject(service['otp']) ?? {};
      const group = groupNames.get(asString(service['groupId'])) ?? '';
      return {
        kind: asString(otp['tokenType']).toLowerCase() || 'totp',
        // `name` est le service tel que l'utilisateur l'a renomme dans 2FAS ; `otp.issuer` celui du QR code.
        issuer: asString(service['name']) || asString(otp['issuer']),
        account: asString(otp['account']),
        secret: asString(service['secret']),
        algorithm: asString(otp['algorithm']),
        digits: asString(otp['digits']),
        period: asString(otp['period']),
        counter: asString(otp['counter']),
        notes: '',
        favorite: false,
        tags: group.length > 0 ? [group] : [],
      };
    });
  return entriesOf(accounts);
}

/** Derivation fixee par 2FAS (absente du fichier) : PBKDF2-HMAC-SHA256, 10 000 tours, cle de 256 bits. */
const TWOFAS_PBKDF2_ITERATIONS = 10_000;

/**
 * Rend les services en clair d'une sauvegarde 2FAS chiffree :
 * `servicesEncrypted` = « chiffre+tag : sel : nonce », chaque partie en base64.
 *
 * @throws {ImportPasswordRequiredError} mot de passe non fourni
 * @throws {ImportPasswordInvalidError} tag GCM refuse
 */
async function decryptTwoFasServices(sealed: string, password: string | undefined, onProgress?: (fraction: number) => void): Promise<readonly Json[]> {
  const parts = sealed.split(':');
  if (parts.length < 3) throw new Error('sauvegarde 2FAS : structure inattendue');
  if (password === undefined) throw new ImportPasswordRequiredError('2FAS chiffre');
  const data = fieldBytes(parts[0], 'base64', 'sauvegarde 2FAS');
  const salt = fieldBytes(parts[1], 'base64', 'sauvegarde 2FAS');
  const nonce = fieldBytes(parts[2], 'base64', 'sauvegarde 2FAS', 12);
  // Tampons non partages (`BufferSource` exclut `SharedArrayBuffer`).
  const secret = new TextEncoder().encode(password);
  const material = await crypto.subtle.importKey('raw', secret, 'PBKDF2', false, ['deriveBits']);
  wipe(secret);
  const key = new Uint8Array(await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt: new Uint8Array(salt), iterations: TWOFAS_PBKDF2_ITERATIONS }, material, 256));
  // WebCrypto ne dit pas son avancement : seule la fin est signalee.
  onProgress?.(1);
  const plain = openGcm(key, nonce, data);
  wipe(key);
  if (plain === null) throw new ImportPasswordInvalidError('2FAS');
  try {
    const services = JSON.parse(bytesToUtf8(plain)) as Json;
    if (!Array.isArray(services)) throw new Error('liste attendue');
    return services;
  } catch {
    throw new Error('sauvegarde 2FAS : contenu dechiffre illisible');
  } finally {
    wipe(plain);
  }
}

// --- Proton Authenticator --------------------------------------------------

/**
 * Derivation fixee par Proton Authenticator (absente du fichier, comme chez
 * 2FAS) : Argon2id v1.3, 19 Mio, 2 passes, 1 voie, cle de 256 bits ; le
 * contenu est « nonce (12) + chiffre + tag » en AES-256-GCM, lie a cette
 * donnee associee. Aucun parametre ne vient du fichier : un fichier forge ne
 * peut pas faire geler l'onglet.
 */
const PROTON_AUTH_ARGON2 = { t: 2, m: 19 * 1024, p: 1, dkLen: 32 } as const;
const PROTON_AUTH_AAD = 'proton.authenticator.export.v1';
const PROTON_AUTH_NONCE_LENGTH = 12;

function protonAuthenticatorEntries(file: Record<string, Json>): readonly Json[] | null {
  const entries = file['entries'];
  if (!Array.isArray(entries)) return null;
  return entries.every((entry) => typeof asObject(asObject(entry)?.['content'])?.['uri'] === 'string') ? entries : null;
}

/**
 * Un export Proton Authenticator se reconnait a son `version` numerique et,
 * en clair, a des `entries` dont chaque `content` porte une `uri` ; chiffre,
 * au couple `salt` + `content` en chaines (sans le `data` d'un export
 * Bitwarden protege, qui porte aussi un `salt`).
 */
export function isProtonAuthenticatorExport(data: Json): boolean {
  const file = asObject(data);
  if (file === null || typeof file['version'] !== 'number') return false;
  if (typeof file['salt'] === 'string' && typeof file['content'] === 'string') return !('data' in file);
  return protonAuthenticatorEntries(file) !== null;
}

/**
 * Rend l'export en clair d'un export Proton Authenticator chiffre. La cle
 * derivee et le clair sont effaces apres usage.
 *
 * @throws {ImportPasswordRequiredError} mot de passe non fourni
 * @throws {ImportPasswordInvalidError} tag GCM refuse
 */
async function decryptProtonAuthenticator(file: Record<string, Json>, password: string | undefined, onProgress?: (fraction: number) => void): Promise<Json> {
  if (file['version'] !== 1) throw new Error(`export Proton Authenticator chiffre : version ${asString(file['version'])} non prise en charge`);
  const salt = fieldBytes(file['salt'], 'base64', 'export Proton Authenticator');
  const content = fieldBytes(file['content'], 'base64', 'export Proton Authenticator');
  if (content.length <= PROTON_AUTH_NONCE_LENGTH + 16) throw new Error('export Proton Authenticator : structure inattendue');
  if (password === undefined) throw new ImportPasswordRequiredError('Proton Authenticator chiffre');
  const secret = utf8ToBytes(password);
  const key = await argon2idAsync(secret, salt, { ...PROTON_AUTH_ARGON2, asyncTick: KDF_ASYNC_TICK_MS, onProgress });
  wipe(secret);
  const plain = openGcm(key, content.subarray(0, PROTON_AUTH_NONCE_LENGTH), content.subarray(PROTON_AUTH_NONCE_LENGTH), utf8ToBytes(PROTON_AUTH_AAD));
  wipe(key);
  if (plain === null) throw new ImportPasswordInvalidError('Proton Authenticator');
  try {
    return JSON.parse(bytesToUtf8(plain)) as Json;
  } catch {
    throw new Error('export Proton Authenticator : contenu dechiffre illisible');
  } finally {
    wipe(plain);
  }
}

/**
 * `steam://<graine base32>` : la forme de Proton Authenticator pour Steam, qui
 * n'y garde ni emetteur ni compte.
 */
function parseSteamUri(uri: string): AuthenticatorAccount {
  return {
    kind: 'steam',
    issuer: 'Steam',
    account: '',
    secret: uri.replace(/^steam:\/\//iu, '').replace(/[/?#].*$/u, '').trim(),
    algorithm: '',
    digits: '5',
    period: '',
    counter: '',
    notes: '',
    favorite: false,
    tags: [],
  };
}

/**
 * Parse un export Proton Authenticator, en clair ou chiffre (ouvert avec
 * `password`) : chaque entree porte l'URI `otpauth://` (ou `steam://`) du
 * compte, son nom affiche (`content.name`, prioritaire sur le compte du
 * libelle, comme l'importeur d'Aegis) et sa note.
 *
 * @throws {ImportPasswordRequiredError} export chiffre sans mot de passe
 * @throws {ImportPasswordInvalidError} mot de passe refuse
 */
export async function parseProtonAuthenticatorExport(data: Json, password?: string, onProgress?: (fraction: number) => void): Promise<ImportedEntry[]> {
  let file = asObject(data);
  if (file !== null && typeof file['salt'] === 'string' && typeof file['content'] === 'string') file = asObject(await decryptProtonAuthenticator(file, password, onProgress));
  const entries = file === null ? null : protonAuthenticatorEntries(file);
  if (entries === null) throw new Error('export Proton Authenticator : structure inattendue');
  const accounts = entries.map((entry): AuthenticatorAccount => {
    const record = asObject(entry)!;
    const content = asObject(record['content'])!;
    const uri = asString(content['uri']);
    const parsed = /^steam:\/\//iu.test(uri) ? parseSteamUri(uri) : parseOtpauthUri(uri);
    const name = asString(content['name']);
    return {
      ...parsed,
      account: name || parsed.account,
      notes: typeof record['note'] === 'string' ? record['note'].trim() : '',
    };
  });
  return entriesOf(accounts);
}

// --- Liste d'URI (otpauth:// et otpauth-migration://) -----------------------

/**
 * Lecteur protobuf minimal (varint, longueur prefixee, 32/64 bits) : juste ce
 * qu'il faut pour `MigrationPayload` de Google Authenticator. Aucune crypto :
 * le transfert de Google n'est pas chiffre, c'est un encodage.
 */
class ProtoReader {
  private offset = 0;

  constructor(private readonly bytes: Uint8Array) {}

  get done(): boolean {
    return this.offset >= this.bytes.length;
  }

  /** Varint brut (64 bits) : un int32 negatif, comme le `batch_id` de Google, s'y code sur 10 octets. */
  varintBig(): bigint {
    let result = 0n;
    for (let shift = 0n; shift < 70n; shift += 7n) {
      if (this.offset >= this.bytes.length) throw new Error('migration Google Authenticator tronquee');
      const byte = this.bytes[this.offset++]!;
      result |= BigInt(byte & 0x7f) << shift;
      if ((byte & 0x80) === 0) return result;
    }
    throw new Error('migration Google Authenticator : entier illisible');
  }

  varint(): number {
    return safeProtoNumber(this.varintBig());
  }

  take(length: number): Uint8Array {
    if (length < 0 || this.offset + length > this.bytes.length) throw new Error('migration Google Authenticator tronquee');
    const out = this.bytes.subarray(this.offset, this.offset + length);
    this.offset += length;
    return out;
  }

  /** Champ suivant : numero, type de fil, et sa valeur (nombre ou octets). */
  field(): { readonly id: number; readonly value: bigint | Uint8Array } {
    const key = this.varint();
    const id = Math.floor(key / 8);
    switch (key % 8) {
      case 0:
        return { id, value: this.varintBig() };
      case 1:
        return { id, value: this.take(8) };
      case 2:
        return { id, value: this.take(this.varint()) };
      case 5:
        return { id, value: this.take(4) };
      default:
        throw new Error('migration Google Authenticator : champ illisible');
    }
  }
}

/** int64 du compteur HOTP : au-dela de 2^53, le nombre n'est plus exact. */
function safeProtoNumber(value: bigint): number {
  if (value > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error('migration Google Authenticator : entier hors borne');
  return Number(value);
}

/** `Algorithm` de `OtpParameters` (0 = non precise : SHA1, comme Google Authenticator). */
const GOOGLE_ALGORITHMS: Readonly<Record<number, string>> = { 0: 'SHA1', 1: 'SHA1', 2: 'SHA256', 3: 'SHA512', 4: 'MD5' };
/** `DigitCount` de `OtpParameters` (0 = non precise : 6). */
const GOOGLE_DIGITS: Readonly<Record<number, string>> = { 0: '6', 1: '6', 2: '8' };

/** `OtpParameters` : 1 secret, 2 name, 3 issuer, 4 algorithm, 5 digits, 6 type (1 HOTP, 2 TOTP), 7 counter. */
function parseGoogleOtpParameters(bytes: Uint8Array): AuthenticatorAccount {
  const reader = new ProtoReader(bytes);
  const text = new TextDecoder();
  let secret = '';
  let name = '';
  let issuer = '';
  let algorithm = 0;
  let digits = 0;
  let type = 0;
  let counter = 0;
  while (!reader.done) {
    const { id, value } = reader.field();
    if (id === 1 && value instanceof Uint8Array) secret = value.length > 0 ? bytesToBase32(value) : '';
    else if (id === 2 && value instanceof Uint8Array) name = text.decode(value).trim();
    else if (id === 3 && value instanceof Uint8Array) issuer = text.decode(value).trim();
    else if (id === 4 && typeof value === 'bigint') algorithm = safeProtoNumber(value);
    else if (id === 5 && typeof value === 'bigint') digits = safeProtoNumber(value);
    else if (id === 6 && typeof value === 'bigint') type = safeProtoNumber(value);
    else if (id === 7 && typeof value === 'bigint') counter = safeProtoNumber(value);
  }
  // Sans emetteur, Google range parfois « Emetteur:compte » dans le nom.
  const separator = name.indexOf(':');
  const prefix = separator > 0 ? name.slice(0, separator).trim() : '';
  if (issuer.length === 0 && prefix.length > 0) issuer = prefix;
  const account = separator > 0 && prefix === issuer ? name.slice(separator + 1).trim() : name;
  return {
    kind: type === 1 ? 'hotp' : 'totp',
    issuer,
    account,
    secret,
    algorithm: GOOGLE_ALGORITHMS[algorithm] ?? `algorithme-${algorithm}`,
    digits: GOOGLE_DIGITS[digits] ?? `chiffres-${digits}`,
    period: '',
    counter: String(counter),
    notes: '',
    favorite: false,
    tags: [],
  };
}

/** Un transfert de Google Authenticator met une dizaine de comptes par code QR : 1 000 codes, c'est 10 000 comptes. */
const MAX_GOOGLE_BATCH = 1000;

/** Place d'un QR code dans un transfert Google Authenticator en plusieurs codes (`batch_*` de `MigrationPayload`). */
interface GoogleMigrationBatch {
  readonly id: number;
  readonly index: number;
  readonly size: number;
}

/**
 * `otpauth-migration://offline?data=<MigrationPayload en base64>` ; champ 1 repete = un compte ; 3 `batch_size`,
 * 4 `batch_index`, 5 `batch_id` (int32, souvent negatif) disent la place du code dans un gros transfert.
 */
function parseGoogleMigrationUri(line: string): { readonly accounts: AuthenticatorAccount[]; readonly batch: GoogleMigrationBatch } {
  let data: string;
  try {
    // Un `+` du base64 non echappe se lit comme une espace dans une requete : on le remet.
    data = (new URL(line).searchParams.get('data') ?? '').replace(/ /gu, '+');
  } catch {
    throw new Error('migration Google Authenticator illisible');
  }
  if (data.length === 0) throw new Error('migration Google Authenticator sans donnees');
  let payload: Uint8Array;
  try {
    payload = base64ToBytes(data.replace(/=+$/u, '') + '='.repeat((4 - (data.replace(/=+$/u, '').length % 4)) % 4));
  } catch {
    throw new Error('migration Google Authenticator illisible');
  }
  try {
    const reader = new ProtoReader(payload);
    const accounts: AuthenticatorAccount[] = [];
    let batch = { id: 0, index: 0, size: 1 };
    while (!reader.done) {
      const { id, value } = reader.field();
      if (id === 1 && value instanceof Uint8Array) accounts.push(parseGoogleOtpParameters(value));
      else if (id >= 3 && id <= 5 && typeof value === 'bigint') {
        const number = Number(BigInt.asIntN(32, value));
        batch = id === 3 ? { ...batch, size: number } : id === 4 ? { ...batch, index: number } : { ...batch, id: number };
      }
    }
    return { accounts, batch };
  } finally {
    // Le payload porte les graines en clair : il ne survit pas a la lecture.
    wipe(payload);
  }
}

/** URI `otpauth://<type>/<libelle>?secret=...` : libelle « Emetteur:compte », `issuer` prioritaire. */
function parseOtpauthUri(line: string): AuthenticatorAccount {
  let url: URL;
  try {
    url = new URL(line);
  } catch {
    throw new Error('URI otpauth illisible');
  }
  const label = decodeURIComponent(url.pathname.replace(/^\/+/u, ''));
  const separator = label.indexOf(':');
  const issuer = (url.searchParams.get('issuer') ?? '').trim() || (separator > 0 ? label.slice(0, separator).trim() : '');
  const account = (separator >= 0 ? label.slice(separator + 1) : label).trim();
  const display = enteCodeDisplay(url);
  return {
    kind: url.hostname.toLowerCase(),
    issuer,
    account,
    secret: (url.searchParams.get('secret') ?? '').trim(),
    algorithm: (url.searchParams.get('algorithm') ?? '').trim(),
    digits: (url.searchParams.get('digits') ?? '').trim(),
    period: (url.searchParams.get('period') ?? '').trim(),
    counter: (url.searchParams.get('counter') ?? '').trim(),
    notes: display.notes,
    favorite: display.favorite,
    tags: display.tags,
    ...(display.archived ? { archived: true } : {}),
  };
}

/**
 * `codeDisplay` qu'Ente Auth ajoute a chaque URI de son export texte (JSON :
 * `pinned`, `trashed`, `tags`, `note`...) : l'epingle devient le favori, les
 * etiquettes et la note sont repris, un code mis a la corbeille va aux
 * archives. Illisible ou absent : ignore, comme le fait Ente (`safeParsing`).
 */
function enteCodeDisplay(url: URL): { readonly notes: string; readonly favorite: boolean; readonly tags: readonly string[]; readonly archived: boolean } {
  const none = { notes: '', favorite: false, tags: [], archived: false };
  const raw = url.searchParams.get('codeDisplay');
  if (raw === null) return none;
  let display: Record<string, Json> | null;
  try {
    display = asObject(JSON.parse(raw) as Json);
  } catch {
    return none;
  }
  if (display === null) return none;
  return {
    notes: typeof display['note'] === 'string' ? display['note'].trim() : '',
    favorite: display['pinned'] === true,
    tags: (Array.isArray(display['tags']) ? display['tags'] : []).map(asString).filter((tag) => tag.length > 0),
    archived: display['trashed'] === true,
  };
}

function uriLines(text: string): string[] {
  return text
    .replace(/^﻿/u, '')
    .split(/\r?\n/u)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/**
 * Texte dont CHAQUE ligne non vide est une URI `otpauth://` ou
 * `otpauth-migration://` (au moins une) : export texte d'un authentificateur.
 */
export function isAuthenticatorUriList(text: string): boolean {
  const lines = uriLines(text);
  return lines.length > 0 && lines.every((line) => /^otpauth(-migration)?:\/\//iu.test(line));
}

/**
 * Parse une liste d'URI d'authentificateur : chaque `otpauth://` est un
 * compte, chaque `otpauth-migration://` (un QR code du transfert Google
 * Authenticator, plusieurs pour un gros transfert) en porte plusieurs.
 */
export function parseAuthenticatorUriList(text: string): ImportedEntry[] {
  const accounts: AuthenticatorAccount[] = [];
  const batches = new Map<number, { size: number; seen: Set<number> }>();
  // Le meme QR code lu deux fois (deux captures, ligne recopiee) n'importe pas ses comptes deux fois.
  for (const line of new Set(uriLines(text))) {
    if (/^otpauth-migration:\/\//iu.test(line)) {
      const { accounts: transferred, batch } = parseGoogleMigrationUri(line);
      accounts.push(...transferred);
      // Taille forgee (2^31 codes) : refusee avant d'enumerer les codes attendus.
      if (batch.size > MAX_GOOGLE_BATCH || batch.index < 0 || batch.index >= Math.max(batch.size, 1)) {
        throw new Error('migration Google Authenticator illisible');
      }
      if (batch.size > 1) {
        const known = batches.get(batch.id) ?? { size: batch.size, seen: new Set<number>() };
        known.seen.add(batch.index);
        batches.set(batch.id, known);
      }
    } else accounts.push(parseOtpauthUri(line));
  }
  // Un gros transfert tient en plusieurs QR codes : un code oublie, ce sont des comptes perdus sans le savoir.
  for (const { size, seen } of batches.values()) {
    const missing = Array.from({ length: size }, (_, index) => index).filter((index) => !seen.has(index));
    if (missing.length > 0) {
      throw new Error(
        `transfert Google Authenticator incomplet : il manque le(s) code(s) QR ${missing.map((index) => index + 1).join(', ')} sur ${size} -- ajoutez chaque code du transfert, rien n'a ete importe`,
      );
    }
  }
  return entriesOf(accounts);
}
